(function () {
  if (window.customElements && customElements.get('narrator-guide')) return;

  class NarratorGuide extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._timers = [];
      var run = this.setup.bind(this);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }

    setup() {
      var cfgEl = this.querySelector('[data-narrator-config]');
      if (!cfgEl) return;
      try { this.config = JSON.parse(cfgEl.textContent); } catch (e) { return; }
      this.blocks = this.config.blocks || [];
      this.opts = this.config.settings || {};
      if (!this.blocks.length) { this.style.display = 'none'; return; }

      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.bubble = this.querySelector('.narrator__bubble');
      this.nameEl = this.querySelector('.narrator__name');
      this.textEl = this.querySelector('.narrator__text');
      this.srEl = this.querySelector('[data-narrator-sr]');
      this.poses = Array.prototype.slice.call(this.querySelectorAll('.narrator__pose'));
      this.current = 0; // matches the server-rendered first pose + bubble
      this.editorLock = false;
      // Peek and tuck (owner 2026-10-27): the character rests tucked at the screen edge and comes out with a line.
      this.peek = this.classList.contains('narrator--peek');
      this.quiet = false; // peek mode, dismissed or not woken yet: only a tap brings the character out
      this._shown = {}; // peek mode: lines already brought out where they could be seen (each comes out once)
      this._scrolling = false; // peek mode: the page is moving (no pause for 0.6s yet)
      if (this.peek) this.classList.add('is-tucked');

      if (!this.designMode && this.opts.rememberDismiss) {
        try {
          if (sessionStorage.getItem(this.key())) {
            if (this.peek) this.quiet = true;
            else { this.measureShift(); this.classList.add('is-collapsed'); }
          }
        } catch (e) {}
      }
      // Peek: on phones and tablets the character only peeks on load; the visitor's first tap wakes it, and from then
      // on it comes out with each section's line by itself (owner 2026-10-27: "On load intro Lumi only peeks until
      // customer click then it starts it's current behavior"). Computers (990px+, where it never tucks away either)
      // greet on load without a tap (owner 2026-10-27: "Greet without a tap", computers only). Asleep is quiet for this
      // page view only (never remembered); not in the editor.
      this.asleep = false;
      var wide = window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
      if (this.peek && !this.designMode && !this.quiet && !wide) { this.quiet = true; this.asleep = true; }

      this.bindControls();
      this.collect();

      // Top positions hang just below the sticky header and ride up as the announcement bar scrolls away.
      this.topAnchored = this.classList.contains('narrator--top');
      this.vOffset = parseFloat(this.opts.vOffset) || 0;

      var self = this;
      // Announce the first dialog once, and (optionally) type it out after the entrance.
      var first = this.blocks[0] || { name: '', text: '' };
      this.announce(first, !this.peek); // peek: read out when the character comes out with it
      if (this.peek) {
        // Peek mode: an awake character (the theme editor; the storefront starts asleep, see above) comes out with the
        // first line once it has arrived on screen. Timed from the start-up frame (the one that adds is-ready): in a
        // background tab timers run while frames wait, so a timer started now could bring the line out while the
        // character is still hidden, and it would not count as shown.
        requestAnimationFrame(function () {
          if (self.isConnected) self._timers.push(setTimeout(function () { if (self.current === 0) self.pop(true); }, 450));
        });
      } else if (this.opts.typewriter && !this.reduce && (first.text || '')) {
        this.lockSize(first.text || ''); // reserve the final box size before typing so nothing shifts
        this._timers.push(setTimeout(function () {
          if (self.current === 0) self.type(first.text || '');
        }, 450));
      } else {
        this.linkify(); // no typing: the server-rendered first line is already finished
      }

      this.checkOverlay();
      this.positionTop();
      requestAnimationFrame(function () {
        self.classList.add('is-ready');
        self.updateActive(); // seed the correct dialog if the page loaded already scrolled
        self.positionTop();
        self.checkEnd();
      });

      this.onScroll = function () {
        if (self.peek) self.noteScroll(); // every scroll event, not once a frame: it times the pause
        if (self._raf) return;
        self._raf = requestAnimationFrame(function () {
          self._raf = 0;
          // All layout READS first (updateActive/measureTop/checkEnd), the
          // style WRITE last — the old read/write/read order forced a
          // synchronous reflow every scrolled frame.
          self.updateActive();
          var m = self.measureTop();
          self.checkEnd();
          self.applyTop(m);
          if (self.peek) self.checkOverlay(); // the cookie banner can come and go without a body change
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.onResize = this.debounce(function () {
        self.unlockSize();
        self.fitWidth();
        self.collect();
        self.updateActive();
        self.positionTop();
        self.checkEnd();
      }, 200);
      window.addEventListener('resize', this.onResize);
      // Peek: the box's fit is measured in the line's own font, so measure again once a font arrives.
      if (this.peek && document.fonts && document.fonts.addEventListener) {
        this.onFonts = function () { self.fitWidth(); };
        document.fonts.addEventListener('loadingdone', this.onFonts);
      }

      this.onLoad = function () { self.collect(); self.updateActive(); self.positionTop(); self.checkEnd(); };
      window.addEventListener('load', this.onLoad);

      // Hide while a menu/cart drawer (or search modal) is open — Dawn locks body scroll with an "overflow-hidden*" class.
      this.bodyObserver = new MutationObserver(function () { self.checkOverlay(); });
      // Peek mode also watches for the cookie banner being added (see consentUp).
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: !!this.peek });

      this.bindMutation();
      if (this.designMode) this.bindEditor();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onFonts && document.fonts) document.fonts.removeEventListener('loadingdone', this.onFonts);
      if (this.onLoad) window.removeEventListener('load', this.onLoad);
      if (this._onSel) document.removeEventListener('shopify:block:select', this._onSel);
      if (this._onDesel) document.removeEventListener('shopify:block:deselect', this._onDesel);
      if (this.mo) this.mo.disconnect();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this._pcObs) this._pcObs.disconnect();
      if (this._hintWrap && this._hintWrap.parentNode) this._hintWrap.parentNode.removeChild(this._hintWrap);
      if (this._raf) cancelAnimationFrame(this._raf);
      (this._timers || []).forEach(clearTimeout);
      clearTimeout(this._swapT);
      clearTimeout(this._tuckT);
      clearTimeout(this._idleT);
      clearTimeout(this._overlayT);
      this._overlayT = null;
      clearInterval(this._typeT);
    }

    key() { return 'narrator-dismissed-' + this.config.id; }

    debounce(fn, wait) {
      var t;
      return function () {
        var a = arguments, c = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(c, a); }, wait);
      };
    }

    collect() {
      var main = document.getElementById('MainContent') || document.body;
      var self = this.closest('.shopify-section');
      var scoped = main.querySelectorAll(':scope > .shopify-section');
      var list = scoped.length ? scoped : main.querySelectorAll('.shopify-section');
      this.sections = Array.prototype.slice.call(list).filter(function (s) {
        return s !== self && !s.querySelector('narrator-guide') && s.offsetParent !== null && s.offsetHeight > 4;
      });
      this.triggers = Array.prototype.slice.call(document.querySelectorAll('[data-narrator-trigger]'));
      this.endMarkers = Array.prototype.slice.call(document.querySelectorAll('[data-narrator-end]'));
      // Anchored dialogs / end resolve against the FULL top-level section list —
      // every section exactly as the theme-editor sidebar shows it, INCLUDING
      // zero-height ones (wave dividers, empty app blocks) and this narrator.
      // The filtered "sections" list above shrinks with runtime rendering, which
      // silently shifted anchor numbers off the merchant's intent. A 0-height
      // section still has a flow position, so anchorY() on it is exact.
      // Old marker elements keep working alongside — both sources are merged in
      // activeFromTriggers()/checkEnd().
      var secs = Array.prototype.slice.call(list);
      function resolve(a) {
        if (!a || !a.section) return null;
        var sn = Math.floor(a.section);      // guard fractional input: secs[1.5] is undefined
        if (!(sn >= 1) || sn > secs.length) return null;
        return { el: secs[sn - 1], edge: a.edge === 'bottom' ? 'bottom' : 'top', offset: parseFloat(a.offset) || 0 };
      }
      this.anchors = [];
      for (var bi = 0; bi < this.blocks.length; bi++) {
        var ref = resolve(this.blocks[bi].anchor);
        if (ref) this.anchors.push({ step: bi + 1, ref: ref });
      }
      this.endAnchor = resolve(this.opts.end);
      this.renderHints();
    }

    // The document-Y a resolved anchor fires at (viewport-relative, like a marker's rect.top).
    anchorY(ref) {
      var r = ref.el.getBoundingClientRect();
      return (ref.edge === 'bottom' ? r.bottom : r.top) + ref.offset;
    }

    // Theme-editor only: dashed hint chips at each resolved anchor, so transition
    // points stay visible exactly like the old Narrator Trigger sections were.
    renderHints() {
      if (!this.designMode) return;
      if (!this._hintWrap) {
        this._hintWrap = document.createElement('div');
        this._hintWrap.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:0;overflow:visible;z-index:999;pointer-events:none;';
        document.body.appendChild(this._hintWrap);
      }
      var wrap = this._hintWrap;
      wrap.innerHTML = '';
      var mk = function (docY, label, bg) {
        var d = document.createElement('div');
        d.style.cssText = 'position:absolute;left:0;width:100%;text-align:center;border-top:1px dashed rgba(17,17,17,.35);top:' + Math.round(docY) + 'px;';
        var s = document.createElement('span');
        s.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border:1px dashed rgba(17,17,17,.3);border-radius:999px;background:' + bg + ';color:rgba(17,17,17,.6);font-size:12px;line-height:1;white-space:nowrap;transform:translateY(-50%);';
        s.textContent = label;
        d.appendChild(s);
        wrap.appendChild(d);
      };
      for (var i = 0; i < (this.anchors || []).length; i++) {
        mk(this.anchorY(this.anchors[i].ref) + window.scrollY, '💬 Dialog ' + (this.anchors[i].step) + ' starts here', 'rgba(255,143,171,.9)');
      }
      if (this.endAnchor) {
        mk(this.anchorY(this.endAnchor) + window.scrollY, '🛑 Says goodbye here', 'rgba(255,255,255,.92)');
      }
    }

    // For top positions: sit just below the live bottom edge of the sticky header.
    // The header is sticky, so its bottom drops from (announcement bar + header) to (header)
    // as the announcement bar scrolls away — making the character ride up to follow it.
    positionTop() {
      this.applyTop(this.measureTop());
    }

    // The live bottom edge of the sticky header (0 once it has scrolled away or hidden).
    headerBottom() {
      if (!this.headerEl || !this.headerEl.isConnected) {
        this.headerEl = document.querySelector('.section-header')
          || document.querySelector('.shopify-section-header')
          || document.querySelector('.header-wrapper');
      }
      var hb = 0;
      if (this.headerEl) {
        var r = this.headerEl.getBoundingClientRect();
        if (r.bottom > 0) hb = r.bottom;
      }
      return hb;
    }

    measureTop() {
      if (!this.topAnchored) return null;
      var hb = this.headerBottom();
      // While minimized, the rise distance depends on the bubble's height, which changes
      // as dialogs swap on scroll — re-measure each frame (reads grouped before writes).
      var shift = null;
      if (this.classList.contains('is-collapsed') && this.bubble) {
        shift = Math.max(0, this.bubble.offsetHeight + 14 - 18);
      }
      return { top: Math.round(hb + this.vOffset) + 'px', shift: shift };
    }

    applyTop(m) {
      if (!m) return;
      if (this.style.top !== m.top) this.style.top = m.top; // skip no-op writes
      if (m.shift !== null) this.style.setProperty('--nr-shift', m.shift + 'px');
    }

    // Reserve the bubble's FINAL size before the typewriter runs, so the box (and the
    // character below it) don't grow line-by-line while typing. Measured while the
    // bubble is faded out, so the full message never flashes.
    lockSize(text) {
      this.unlockSize();
      // Peek shows each line whole (no typing), so there is nothing to reserve; a locked width is rounded to whole
      // pixels and wrapped lines that just fit ("Pinky / promise!", owner 2026-10-27: "an extra empty line").
      if (this.peek || !this.opts.typewriter || this.reduce || !text) return;
      this.textEl.textContent = text;
      this.linkify(); // measure with the finished line's (bolder) link text
      var w = this.bubble.offsetWidth;
      var h = this.bubble.offsetHeight;
      this.bubble.style.width = w + 'px';
      this.bubble.style.minHeight = h + 'px';
      this.textEl.textContent = '';
    }

    unlockSize() {
      this.bubble.style.width = '';
      this.bubble.style.minHeight = '';
    }

    // Peek: the box hugs its longest line, so a line that wraps leaves no room on the right (owner 2026-10-27: "narrow
    // down the bubbles when there's a lot of space to the right"). Measured on a hidden twin of the box at its widest,
    // which never moves (coming out scales the box, and WebKit measures through that); one line already hugs its text.
    // The extra pixel keeps every line breaking where it did.
    fitWidth() {
      if (!this.peek || !this.bubble) return;
      var bub = this.bubble;
      bub.style.width = '';
      if (!(this.textEl.textContent || '').trim()) return;
      var m = this._measure;
      if (!m || !m.isConnected) {
        m = this._measure = document.createElement('div');
        m.className = 'narrator__bubble narrator__measure';
        m.setAttribute('aria-hidden', 'true');
        bub.parentNode.appendChild(m);
      }
      m.style.width = getComputedStyle(bub).maxWidth;
      m.textContent = '';
      m.appendChild(this.textEl.cloneNode(true));
      var r = document.createRange();
      r.selectNodeContents(m.firstChild);
      var rects = r.getClientRects(), tops = {}, lines = 0, left = Infinity, right = -Infinity;
      for (var i = 0; i < rects.length; i++) {
        if (!rects[i].width) continue;
        var t = Math.round(rects[i].top);
        if (!tops[t]) { tops[t] = 1; lines++; }
        left = Math.min(left, rects[i].left);
        right = Math.max(right, rects[i].right);
      }
      if (lines > 1) {
        var cs = getComputedStyle(bub);
        var edge = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
        bub.style.width = (Math.ceil(right - left + edge) + 1) + 'px';
      }
    }

    // Peek: is-on-dark when the page behind the box is dark (the Comic box then outlines in gold; owner 2026-10-27:
    // "Make the comic outline gold on navy sections"). Three points under the box, each read as the first background
    // painted behind it (a colour, or a gradient's last solid colour); dark when at least two are. Set as the line is
    // set, so the box comes out already in its colour (it only comes out at rest, so what is behind it holds).
    checkBackdrop() {
      if (!this.peek || !document.elementsFromPoint) return;
      // the box's resting place from layout (coming out scales the box itself)
      var self = this, bub = this.bubble, op = bub.offsetParent;
      if (!op) return;
      var o = op.getBoundingClientRect();
      var r = { left: o.left + bub.offsetLeft, top: o.top + bub.offsetTop, width: bub.offsetWidth, height: bub.offsetHeight };
      var parse = function (s) {
        var m = s && s.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
        return m ? { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] } : null;
      };
      var dark = 0, pts = [[0.5, 0.5], [0.15, 0.25], [0.85, 0.75]];
      for (var i = 0; i < pts.length; i++) {
        var x = r.left + r.width * pts[i][0], y = r.top + r.height * pts[i][1];
        var el = document.elementsFromPoint(x, y).filter(function (e) { return !self.contains(e); })[0];
        for (var e = el; e && e !== document.documentElement; e = e.parentElement) {
          var cs = getComputedStyle(e), c = null;
          if (parseFloat(cs.opacity) < 0.1) continue; // a layer not shown (a hover veil): look behind it
          var bg = parse(cs.backgroundColor);
          if (bg && bg.a >= 0.5) c = bg;
          else if (cs.backgroundImage && cs.backgroundImage !== 'none') {
            var stops = cs.backgroundImage.match(/rgba?\([^)]*\)/g) || [];
            for (var k = stops.length - 1; k >= 0 && !c; k--) { var s = parse(stops[k]); if (s && s.a >= 0.5) c = s; }
          }
          if (c) { if ((0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255 < 0.35) dark++; break; }
        }
      }
      this.classList.toggle('is-on-dark', dark >= 2);
    }

    // Peek mode rests low on the screen, where the cookie banner sits: the character keeps out of the way until the
    // banner has been answered.
    consentUp() {
      var pc = document.getElementById('shopify-pc__banner');
      return !!(pc && pc.offsetHeight > 0 && getComputedStyle(pc).visibility !== 'hidden');
    }

    // Peek mode: watch the cookie banner itself, so it opening or closing (class or style) is seen at once.
    watchConsent() {
      var pc = document.getElementById('shopify-pc__banner');
      if (!pc || pc === this._pcEl || !window.MutationObserver) return;
      if (this._pcObs) this._pcObs.disconnect();
      var self = this;
      this._pcEl = pc;
      this._pcObs = new MutationObserver(function () { self.checkOverlay(); });
      this._pcObs.observe(pc, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
    }

    overlayUp() {
      return /(?:^|\s)overflow-hidden/.test(document.body.className) || (this.peek && this.consentUp());
    }

    checkOverlay() {
      var self = this;
      if (this.peek) this.watchConsent();
      if (this.overlayUp()) {
        clearTimeout(this._overlayT);
        this._overlayT = null;
        this.classList.add('is-suppressed');
      } else if (this.classList.contains('is-suppressed') && !this._overlayT) {
        // The scroll lock lifts the moment a drawer starts to close, and the menu drawer slides for up to
        // 0.6s: come back once it is gone, not beside it (owner 2026-10-25).
        this._overlayT = setTimeout(function () {
          self._overlayT = null;
          if (self.overlayUp()) return; // a drawer or the cookie banner came back meanwhile
          self.classList.remove('is-suppressed');
          // Peek: a line that waited out the drawer or the cookie banner comes out now, unless the page is scrolling.
          if (self.peek && !self._scrolling) self.onPause();
        }, 600);
      }
    }

    // The line an anchor or marker must scroll up to: the character's middle. In peek mode the character rests low on
    // the screen, so it is the middle of what the reader sees instead (below the sticky header): a line belongs to the
    // section filling the screen, from its arrival there (owner 2026-10-27: "Should starts with hero then each section
    // gets it's own"; a fifth of the way down left the last section's line showing well into the next). At the very
    // bottom of the page every anchor counts as reached.
    triggerLine() {
      if (this.peek) {
        var atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
        if (atBottom) return Infinity;
        var hb = this.headerBottom();
        return hb + (window.innerHeight - hb) / 2;
      }
      var stage = this.querySelector('.narrator__stage');
      var sr = stage ? stage.getBoundingClientRect() : null;
      return sr ? (sr.top + sr.height / 2) : (window.innerHeight * 0.5);
    }

    // When the reader reaches a "Narrator End" marker (scrolled up to the character),
    // the character advances to its next line, then fades away for good after a delay.
    checkEnd() {
      // Asleep: no send-off yet (the character keeps peeking and following the sections); the waking tap checks.
      if (this._endTriggered || this.asleep) return;
      var hasMarkers = this.endMarkers && this.endMarkers.length;
      if (!hasMarkers && !this.endAnchor) return;
      var line = this.triggerLine();
      if (hasMarkers) {
        for (var i = 0; i < this.endMarkers.length; i++) {
          if (this.endMarkers[i].getBoundingClientRect().top <= line) {
            this._endTriggered = true;
            this.runEnd(this.endMarkers[i]);
            return;
          }
        }
      }
      if (this.endAnchor && this.anchorY(this.endAnchor) <= line) {
        this._endTriggered = true;
        this.runEndDelay(this.opts.end ? this.opts.end.delay : 3);
      }
    }

    runEnd(marker) {
      var secs = parseFloat(marker.getAttribute('data-narrator-end-delay'));
      this.runEndDelay(secs);
    }

    runEndDelay(secs) {
      // advance to the next dialog (the character's send-off line)
      this.show(Math.min(this.current + 1, this.blocks.length - 1));
      // then fade the character away after the configured number of seconds
      secs = parseFloat(secs);
      if (isNaN(secs) || secs < 0) secs = 3;
      // Peek: counted from when the send-off line first comes out where it can be seen (it waits for a pause).
      var b = this.blocks[this.current] || {};
      if (this.peek && !this.quiet && (b.text || '').trim() && !this._shown[this.current]) { this._endAfter = secs; this._endIdx = this.current; return; }
      this.endIn(secs);
    }

    endIn(secs) {
      var self = this;
      this._timers.push(setTimeout(function () { self.classList.add('is-ended'); }, secs * 1000));
    }

    // Decide which dialog should be active and show it. Two modes:
    //  - sections: the content section nearest the viewport centre drives the line.
    //  - markers : "Narrator Trigger" elements ([data-narrator-trigger]) drive it —
    //    each marker the reader scrolls past advances to the next line (or jumps to
    //    a specific one via data-step).
    updateActive() {
      if (this.editorLock || this._endTriggered) return;
      var hasMarks = (this.triggers && this.triggers.length) || (this.anchors && this.anchors.length);
      var di = (this.opts.trigger === 'markers' && hasMarks)
        ? this.activeFromTriggers()
        : this.activeFromSections();
      if (di != null) this.show(di);
    }

    activeFromSections() {
      if (!this.sections || !this.sections.length) return null;
      var mid = window.innerHeight / 2, best = null, bd = Infinity;
      for (var i = 0; i < this.sections.length; i++) {
        var r = this.sections[i].getBoundingClientRect();
        if (r.height === 0) continue;
        var d = mid < r.top ? r.top - mid : (mid > r.bottom ? mid - r.bottom : 0);
        if (d < bd) { bd = d; best = this.sections[i]; }
      }
      var docH = document.documentElement.scrollHeight;
      if (window.scrollY <= 2) best = this.sections[0];
      else if (window.innerHeight + window.scrollY >= docH - 2) best = this.sections[this.sections.length - 1];
      if (!best) return null;
      return this.mapIndex(this.sections.indexOf(best));
    }

    activeFromTriggers() {
      var docH = document.documentElement.scrollHeight;
      var atBottom = window.innerHeight + window.scrollY >= docH - 2;
      var line;
      if (atBottom) {
        line = Infinity;
      } else {
        line = this.triggerLine();
      }
      // Merge both trigger sources into one page-ordered list. Marker elements
      // without data-step mean "advance"; anchored dialogs always jump to their
      // own line, so mixing sources cannot double-advance.
      var entries = [];
      for (var i = 0; i < this.triggers.length; i++) {
        var st = parseInt(this.triggers[i].getAttribute('data-step'), 10);
        entries.push({ y: this.triggers[i].getBoundingClientRect().top, step: st >= 1 ? st : 0 });
      }
      for (var j = 0; j < (this.anchors || []).length; j++) {
        entries.push({ y: this.anchorY(this.anchors[j].ref), step: this.anchors[j].step });
      }
      entries.sort(function (p, q) { return p.y - q.y; });
      var idx = 0;
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].y > line) continue;
        idx = entries[k].step >= 1 ? entries[k].step - 1 : idx + 1;
      }
      return this.mapIndex(idx);
    }

    mapIndex(idx) {
      var n = this.blocks.length;
      if (!n) return null;
      return this.opts.loop ? ((idx % n) + n) % n : Math.max(0, Math.min(idx, n - 1));
    }

    show(i) {
      if (i === this.current || i < 0 || i >= this.blocks.length) return;
      this.current = i;
      this.poses.forEach(function (p) {
        var on = parseInt(p.dataset.index, 10) === i;
        p.classList.toggle('is-active', on);
        // Non-first poses ship data-src only (F23) — hydrate on first activation.
        if (on) {
          var img = p.querySelector('img[data-src]');
          if (img) {
            var ss = img.getAttribute('data-srcset');
            if (ss) img.setAttribute('srcset', ss);
            img.setAttribute('src', img.getAttribute('data-src'));
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
          }
        }
      });
      // Stop any in-flight typing immediately so the old text never lingers under a new pose.
      clearInterval(this._typeT);
      this.classList.remove('is-talking');
      this.swap(this.blocks[i]);
    }

    // speak === false updates the line without reading it out (peek mode, while the character stays unseen).
    announce(b, speak) {
      this._b = b; // the line being shown, for linkify()
      var name = (b.name || '').trim();
      this.nameEl.textContent = name;
      this.nameEl.hidden = !name;
      this.classList.toggle('is-silent', !(b.text || '').trim());
      if (this.srEl && speak !== false) this.srEl.textContent = (name ? name + '. ' : '') + (b.text || '');
    }

    // Peek mode: a line is read out only when the character really shows it.
    canSpeak() {
      return !this.quiet && !this.classList.contains('is-suppressed') && !this.classList.contains('is-ended') && getComputedStyle(this).visibility !== 'hidden';
    }

    swap(b) {
      var self = this;
      var apply = function () {
        // The incoming line's silence first: a silent line hides the character, which canSpeak() reads.
        if (self.peek) self.classList.toggle('is-silent', !(b.text || '').trim());
        // Peek: a line that arrives mid-scroll is read out when the next pause brings the character out with it, and a
        // line already shown stays in (a resize can step back and forth over the anchors).
        var fresh = self.peek && self.unseen();
        self.announce(b, !self.peek || (fresh && !self._scrolling && self.canSpeak()));
        self.lockSize(b.text || ''); // measured while the bubble is faded out (is-out)
        self.bubble.classList.remove('is-out');
        if (self.peek) self.comeOut(b, fresh);
        self.type(b.text || '');
      };
      if (this.reduce) { apply(); return; }
      this.bubble.classList.add('is-out');
      clearTimeout(this._swapT);
      if (this.peek) clearTimeout(this._tuckT); // the new line takes over; no tuck in between
      this._swapT = setTimeout(apply, Math.max(140, (this.opts.speed || 450) * 0.42));
    }

    type(text) {
      clearInterval(this._typeT);
      this.classList.remove('is-talking');
      // Peek: the whole line at once, no typing, so nothing lags behind the page (owner 2026-10-27).
      if (this.peek) {
        this.textEl.textContent = text;
        this.linkify();
        this.fitWidth();
        this.checkBackdrop();
        if (text) this.tuckLater(); else this.tuck();
        return;
      }
      if (!this.opts.typewriter || this.reduce) { this.textEl.textContent = text; this.linkify(); return; }
      this.textEl.textContent = '';
      if (!text) return;
      this.classList.add('is-talking');
      var i = 0, self = this, sp = Math.max(8, this.opts.typeSpeed || 32);
      this._typeT = setInterval(function () {
        i++;
        self.textEl.textContent = text.slice(0, i);
        if (i >= text.length) {
          clearInterval(self._typeT);
          self.classList.remove('is-talking');
          self.linkify();
        }
      }, sp);
    }

    // Peek mode pops only when the page rests (owner 2026-10-27: "Pops when you pause", after the scroll-driven
    // pop-outs felt "clunky and delayed"): the first scroll event tucks the character at once, and 0.6s without one
    // counts as a pause, which brings out the current line if it has not been shown yet.
    noteScroll() {
      var self = this;
      if (this.editorLock) return; // the theme editor keeps its selected line out
      if (!this._scrolling) { this._scrolling = true; this.tuck(); }
      clearTimeout(this._idleT);
      this._idleT = setTimeout(function () { self._scrolling = false; self.onPause(); }, 600);
    }

    onPause() {
      var b = this.blocks[this.current] || {};
      if (!this.unseen() || this.quiet || !(b.text || '').trim() || !this.canSpeak()) return;
      this.pop(true);
    }

    // Peek: a line comes out by itself once; the theme editor's selected line always does.
    unseen() {
      return this.editorLock || !this._shown[this.current];
    }

    // A line counts as shown once it came out where it could be seen; a waiting send-off starts its countdown then
    // (only the send-off itself: the theme editor can select another line meanwhile).
    markShown() {
      this._shown[this.current] = true;
      if (this._endAfter != null && this.current === this._endIdx) { this.endIn(this._endAfter); this._endAfter = null; }
    }

    // Peek mode: the character comes out with the current line, all at once; once it has had time to be read it
    // tucks away again. `auto` (load, a pause, the page's own cue) respects a dismissal and waits while the page is
    // scrolling; a tap does neither. A line counts as shown once it came out where it could be seen.
    pop(auto) {
      if (!this.peek) return;
      // Behind a drawer or the cookie banner nothing comes out, a tap included (it would count unseen); the line waits
      // and comes out when they clear (checkOverlay's restore).
      if (this.classList.contains('is-suppressed')) return;
      var b = this.blocks[this.current] || {};
      if (!(b.text || '').trim() || (auto && this.quiet)) return;
      if (auto && this._scrolling) return; // the pause will bring it out
      clearTimeout(this._tuckT);
      clearTimeout(this._swapT); // this shows the current line itself
      this.classList.remove('is-silent'); // the line has words; canSpeak() reads the visibility that follows
      var seen = !auto || this.canSpeak();
      this.announce(b, seen);
      if (seen) this.markShown();
      this.lockSize(b.text || '');
      this.bubble.classList.remove('is-out');
      this.classList.remove('is-tucked');
      this.type(b.text || '');
    }

    // A new line arriving while the page scrolls waits, tucked, for the pause; otherwise (the theme editor) it
    // comes out at once, unless it was already shown or dismissed, or the line is silent.
    comeOut(b, fresh) {
      clearTimeout(this._tuckT);
      if (this._scrolling) return;
      // A line already shown goes back in rather than replace the one out (a resize can step back onto it).
      if (!fresh) { this.tuck(); return; }
      if (!this.quiet && (b.text || '').trim()) {
        this.classList.remove('is-tucked');
        if (this.canSpeak()) this.markShown();
      }
    }

    // fromUser: a tuck the visitor asked for also cancels a line about to come out, which would reopen it.
    tuck(fromUser) {
      clearTimeout(this._tuckT);
      if (fromUser) clearTimeout(this._swapT);
      this.classList.add('is-tucked');
    }

    // Tucks 4.5s after a line comes out (or as soon as the page scrolls); not while a mouse rests on it, nor while
    // the theme editor shows a line.
    tuckLater() {
      var self = this;
      clearTimeout(this._tuckT);
      if (this.editorLock || this._hold) return;
      this._tuckT = setTimeout(function () { self.tuck(); }, 4500);
    }

    // A finished line turns its "Linked words" into a real link, built from DOM
    // nodes (never innerHTML). The text span is aria-hidden (the live region
    // announces the line), so the link stays out of the tab order too.
    linkify() {
      var b = this._b, t = this.textEl;
      if (!b || !b.link || !b.linkText) return;
      var text = t.textContent, at = text.indexOf(b.linkText);
      if (at < 0) return;
      var a = document.createElement('a');
      a.className = 'narrator__link';
      a.href = b.link;
      a.tabIndex = -1;
      a.textContent = b.linkText;
      // Follow the link without the dialog box's tap-to-minimize firing too.
      a.addEventListener('click', function (e) { e.stopPropagation(); });
      t.textContent = '';
      t.appendChild(document.createTextNode(text.slice(0, at)));
      t.appendChild(a);
      t.appendChild(document.createTextNode(text.slice(at + b.linkText.length)));
    }

    bindControls() {
      var self = this;
      var minBtn = this.querySelector('.narrator__min');
      var restoreBtn = this.querySelector('.narrator__restore');
      if (minBtn) minBtn.addEventListener('click', function (e) { e.stopPropagation(); self.collapse(); });
      if (restoreBtn) restoreBtn.addEventListener('click', function (e) { e.stopPropagation(); self.expand(); });
      if (this.peek) {
        // Peek mode: the peeking character is the way back to its line (a button for keyboards too); a tap on the
        // dialog box tucks it away; a mouse resting on either keeps it out.
        var peekStage = this.querySelector('.narrator__stage');
        if (peekStage) {
          var toggle = function () {
            // The first tap wakes it (see connectedCallback) and brings out the current line; past the end of the page
            // that is the send-off (with reduced motion checkEnd() can already have it out: never tuck on this tap).
            if (self.asleep) { self.asleep = false; self.quiet = false; self.checkEnd(); self.pop(); return; }
            if (self.classList.contains('is-tucked')) self.pop(); else self.tuck(true);
          };
          peekStage.setAttribute('role', 'button');
          peekStage.setAttribute('aria-label', 'Show message');
          peekStage.tabIndex = 0;
          peekStage.addEventListener('click', toggle);
          peekStage.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
          });
        }
        if (this.bubble) this.bubble.addEventListener('click', function () { self.tuck(true); });
        var hold = function (on) {
          self._hold = on;
          if (on) clearTimeout(self._tuckT);
          else if (!self.classList.contains('is-tucked')) self.tuckLater();
        };
        this.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hold(true); });
        this.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hold(false); });
        return;
      }
      // Tap/click the dialog box or the character to toggle the minimized state.
      if (this.opts.minimize) {
        if (this.bubble) this.bubble.addEventListener('click', function () { self.collapse(); });
        var stage = this.querySelector('.narrator__stage');
        if (stage) stage.addEventListener('click', function () {
          if (self.classList.contains('is-collapsed')) self.expand(); else self.collapse();
        });
      }
    }

    collapse() {
      // Peek mode: the minimise button tucks the character and keeps it in for the rest of the visit.
      if (this.peek) { this.quiet = true; this.tuck(true); this.remember(true); return; }
      if (this.classList.contains('is-collapsed')) return;
      this.measureShift();
      this.classList.add('is-collapsed');
      this.remember(true);
    }

    expand() {
      if (!this.classList.contains('is-collapsed')) return;
      this.classList.remove('is-collapsed');
      this.remember(false);
    }

    remember(collapsed) {
      if (this.designMode || !this.opts.rememberDismiss) return;
      try {
        if (collapsed) sessionStorage.setItem(this.key(), '1');
        else sessionStorage.removeItem(this.key());
      } catch (e) {}
    }

    // Distance the character rises when minimized: the dialog box's height + its gap,
    // minus an 18px buffer so a top-anchored character clears the header instead of
    // butting up against it.
    measureShift() {
      var h = this.bubble ? this.bubble.offsetHeight : 0;
      this.style.setProperty('--nr-shift', Math.max(0, h + 14 - 18) + 'px');
    }

    bindMutation() {
      var main = document.getElementById('MainContent');
      if (!main || !window.MutationObserver) return;
      var self = this;
      this.mo = new MutationObserver(this.debounce(function () { self.collect(); self.updateActive(); }, 300));
      this.mo.observe(main, { childList: true });
    }

    bindEditor() {
      if (this._editorBound) return;
      this._editorBound = true;
      var self = this;
      this._onSel = function (e) {
        if (!e.detail || !e.detail.blockId) return;
        var pose = self.querySelector('.narrator__pose[data-block-id="' + e.detail.blockId + '"]');
        if (!pose) return;
        self.editorLock = true;
        self.show(parseInt(pose.dataset.index, 10));
      };
      this._onDesel = function () {
        self.editorLock = false;
        if (self.peek && !self.classList.contains('is-tucked')) self.tuckLater(); // peek: tuck away again after editing
      };
      document.addEventListener('shopify:block:select', this._onSel);
      document.addEventListener('shopify:block:deselect', this._onDesel);
    }
  }

  customElements.define('narrator-guide', NarratorGuide);
})();
